import React, { useEffect, useState } from 'react';
import Header from '../../../components/Header/Header';
import { Search } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ProjectHead = () => {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState({});
  const [loading, setLoading] = useState(false);
  const [projectTeams, setProjectTeams] = useState({});
  const [globalSearch, setGlobalSearch] = useState('');
  const [userSearches, setUserSearches] = useState({}); // Project-specific user searches for assignment

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, usersRes, teamsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/admin/user/project-heads`),
          fetch(`${API_BASE_URL}/admin/user/users`),
          fetch(`${API_BASE_URL}/admin/user/project-teams`)
        ]);
        
        const projectsData = await projectsRes.json();
        const usersData = await usersRes.json();
        const teamsData = await teamsRes.json();
        
        const teamsMap = teamsData.reduce((acc, team) => {
          if (!acc[team.ProjectID]) {
            acc[team.ProjectID] = [];
          }
          acc[team.ProjectID].push({
            UserID: team.UserID,
            UserName: team.UserName
          });
          return acc;
        }, {});

        setProjects(projectsData);
        setUsers(usersData);
        setProjectTeams(teamsMap);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Filter projects based on project name or team member name
  const filteredProjects = projects.filter(project => {
    const searchTerm = globalSearch.toLowerCase();
    
    // Check if project name matches
    const projectNameMatch = project.ProjectName.toLowerCase().includes(searchTerm);
    
    // Check if any team member name matches
    const teamMembers = projectTeams[project.ProjectID] || [];
    const teamMemberMatch = teamMembers.some(member => 
      member.UserName.toLowerCase().includes(searchTerm)
    );
    
    return projectNameMatch || teamMemberMatch;
  });

  // Rest of the functions remain the same
  const handleUserSelect = (projectId, selectedOptions) => {
    const selectedValues = Array.from(selectedOptions)
      .filter(option => option.selected)
      .map(option => option.value);
    
    setSelectedUsers({
      ...selectedUsers,
      [projectId]: selectedValues
    });
  };

  const handleUserSearch = (projectId, value) => {
    setUserSearches(prev => ({
      ...prev,
      [projectId]: value
    }));
  };

  const getFilteredUsers = (projectId) => {
    const searchTerm = userSearches[projectId] || '';
    return users.filter(user =>
      user.UserName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleAssignProject = async (projectId) => {
    if (!selectedUsers[projectId] || selectedUsers[projectId].length === 0) {
      alert('Please select users to assign');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/user/assign-project-team`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId,
          userIds: selectedUsers[projectId]
        }),
      });

      if (response.ok) {
        const teamsRes = await fetch(`${API_BASE_URL}/admin/user/project-teams`);
        const teamsData = await teamsRes.json();
        
        const teamsMap = teamsData.reduce((acc, team) => {
          if (!acc[team.ProjectID]) {
            acc[team.ProjectID] = [];
          }
          acc[team.ProjectID].push({
            UserID: team.UserID,
            UserName: team.UserName
          });
          return acc;
        }, {});

        setProjectTeams(teamsMap);
        setSelectedUsers(prev => ({
          ...prev,
          [projectId]: []
        }));
        alert('Team members assigned successfully');
      } else {
        throw new Error('Failed to assign team members');
      }
    } catch (error) {
      console.error('Error assigning team members:', error);
      alert('Failed to assign team members');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTeamMember = async (projectId, userId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/user/remove-team-member`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId,
          userId
        }),
      });

      if (response.ok) {
        const updatedTeams = { ...projectTeams };
        updatedTeams[projectId] = updatedTeams[projectId].filter(
          user => user.UserID !== userId
        );
        setProjectTeams(updatedTeams);
        alert('Team member removed successfully');
      } else {
        throw new Error('Failed to remove team member');
      }
    } catch (error) {
      console.error('Error removing team member:', error);
      alert('Failed to remove team member');
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
          Project Team Management
        </h1>
        
        {/* Unified search bar for projects and team members */}
        <div className="mb-6 relative">
          <div className="flex items-center border rounded-lg bg-white p-2 shadow-sm">
            <Search className="w-5 h-5 text-gray-400 mr-2" />
            <input
              type="text"
              placeholder="Search projects or team members..."
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              className="w-full outline-none text-gray-700"
            />
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Search will show projects matching the project name or containing team members matching the search term
          </div>
        </div>

        {Array.isArray(filteredProjects) && filteredProjects.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white shadow-lg rounded-lg overflow-hidden">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold tracking-wider uppercase">
                    Project Name
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold tracking-wider uppercase">
                    Current Team
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold tracking-wider uppercase">
                    Assign Members
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project, index) => (
                  <tr
                    key={index}
                    className={`${
                      index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                    } hover:bg-gray-100`}
                  >
                    <td className="px-6 py-4 text-sm text-gray-800">
                      {project.ProjectName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-800">
                      <div className="flex flex-wrap gap-2">
                        {projectTeams[project.ProjectID]?.map((user) => (
                          <span
                            key={user.UserID}
                            className="inline-flex items-center bg-blue-100 px-2 py-1 rounded"
                          >
                            {user.UserName}
                            <button
                              onClick={() => handleRemoveTeamMember(project.ProjectID, user.UserID)}
                              className="ml-2 text-red-500 hover:text-red-700"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-800">
                      <div className="flex flex-col gap-2">
                        {/* Search bar for assignable users */}
                        <div className="relative">
                          <div className="flex items-center border rounded-md bg-white p-1">
                            <Search className="w-4 h-4 text-gray-400 mr-1" />
                            <input
                              type="text"
                              placeholder="Search users to assign..."
                              value={userSearches[project.ProjectID] || ''}
                              onChange={(e) => handleUserSearch(project.ProjectID, e.target.value)}
                              className="w-full outline-none text-gray-700 text-sm"
                            />
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <select
                            multiple
                            className="border rounded-md p-2 w-64 h-32"
                            value={selectedUsers[project.ProjectID] || []}
                            onChange={(e) => handleUserSelect(project.ProjectID, e.target.options)}
                          >
                            {getFilteredUsers(project.ProjectID).map((user) => (
                              <option
                                key={user.UserID}
                                value={user.UserID}
                              >
                                {user.UserName}
                              </option>
                            ))}
                          </select>
                          
                          <button
                            onClick={() => handleAssignProject(project.ProjectID)}
                            disabled={loading || !selectedUsers[project.ProjectID]?.length}
                            className={`ml-2 px-4 py-2 rounded-md ${
                              loading || !selectedUsers[project.ProjectID]?.length
                                ? 'bg-gray-300 cursor-not-allowed'
                                : 'bg-blue-500 hover:bg-blue-600 text-white'
                            }`}
                          >
                            {loading ? 'Assigning...' : 'Assign'}
                          </button>
                        </div>
                        <div className="text-xs text-gray-500">
                          Hold Ctrl/Cmd to select multiple users
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-600 text-center">No matching projects found.</p>
        )}
      </main>
    </div>
  );
};

export default ProjectHead;